import React, { useCallback, useState } from 'react';
import { MenuItem, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { ContentSafetyActions } from '../../components/ContentSafetyActions';
import { Button, SplitButton } from '../../components/design-system';
import { FIND_SETTINGS_FORM_ID } from '../../components/FindSettings/constants';

interface FindActionBarProps {
  createdBy?: string;
  findId?: string;
  saveDisabled?: boolean;
  shared?: boolean;
  showSave?: boolean;
  siteId?: string;
}

const actionGroupStyles = { alignItems: 'center', flexWrap: 'wrap' };
const createButtonStyles = { px: 2.25 };
const saveButtonStyles = { px: 2.5 };
const CREATE_NEW = 'create-new';
const CLONE_FIND = 'clone-find';
const createOptions = [
  {
    label: (
      <FormattedMessage
        id="find.action.createNew"
        defaultMessage="Create new"
      />
    ),
    value: CREATE_NEW,
  },
  {
    label: (
      <FormattedMessage id="find.action.clone" defaultMessage="Clone find" />
    ),
    value: CLONE_FIND,
  },
];
const findMenuItems = [
  <MenuItem disabled key="export">
    <FormattedMessage
      id="find.action.exportComingSoon"
      defaultMessage="Export (coming soon)"
    />
  </MenuItem>,
  <MenuItem disabled key="duplicate">
    <FormattedMessage
      id="find.action.duplicateComingSoon"
      defaultMessage="Duplicate (coming soon)"
    />
  </MenuItem>,
];

const FindActionBar = ({
  createdBy,
  findId,
  saveDisabled = false,
  shared = false,
  showSave = true,
  siteId,
}: FindActionBarProps) => {
  const navigate = useNavigate();
  const [createAction, setCreateAction] = useState(CREATE_NEW);
  const createActionLabel =
    createOptions.find(({ value }) => value === createAction)?.label || null;

  const handleCreateActionChange = useCallback((value: string) => {
    if (value === CREATE_NEW || value === CLONE_FIND) {
      setCreateAction(value);
    }
  }, []);
  const handleClickCreate = useCallback(() => {
    const createPath = siteId ? `/s/${siteId}/finds/create` : '/f/create';
    const cloneQuery =
      createAction === CLONE_FIND && findId
        ? `?clone=${encodeURIComponent(findId)}`
        : '';

    navigate(`${createPath}${cloneQuery}`);
  }, [createAction, findId, navigate, siteId]);

  return (
    <Stack direction="row" gap={0.75} sx={actionGroupStyles}>
      <SplitButton
        label={createActionLabel}
        menuButtonAriaLabel="Choose create action"
        onClick={handleClickCreate}
        onSelectionChange={handleCreateActionChange}
        options={createOptions}
        startIcon={<AddIcon />}
        sx={createButtonStyles}
        tone="neutral"
        type="button"
        value={createAction}
      />
      {showSave && (
        <Button
          disabled={saveDisabled}
          form={FIND_SETTINGS_FORM_ID}
          sx={saveButtonStyles}
          type="submit"
        >
          <FormattedMessage id="action.save" defaultMessage="Save" />
        </Button>
      )}
      <ContentSafetyActions
        enabled={shared}
        entityId={findId}
        entityType="FIND"
        menuItems={findMenuItems}
        ownerUserId={createdBy}
      />
    </Stack>
  );
};

export { FindActionBar };
